import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Flex, Heading, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Input, Select, useDisclosure, useToast, Spinner, HStack, VStack, Text,
} from '@chakra-ui/react';
import { FiPlus, FiSlash, FiCopy, FiRefreshCw } from 'react-icons/fi';

export default function ApiKeys() {
  const [keys, setKeys] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', organization: '' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const token = localStorage.getItem('meet_token');
  const headers = { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' };

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const [keysRes, orgsRes] = await Promise.all([
        fetch('/api/v1/api-keys/', { headers }),
        fetch('/api/v1/organizations/', { headers }),
      ]);
      if (keysRes.ok) setKeys(await keysRes.json());
      if (orgsRes.ok) setOrgs(await orgsRes.json());
    } catch (e) {
      toast({ title: 'Failed to load', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    try {
      const body = { name: form.name };
      if (form.organization) body.organization = parseInt(form.organization);
      const res = await fetch('/api/v1/api-keys/', {
        method: 'POST', headers, body: JSON.stringify(body),
      });
      if (res.ok) {
        toast({ title: 'API key created', status: 'success', duration: 2000 });
        onClose();
        setForm({ name: '', organization: '' });
        fetchKeys();
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Revoke this API key?')) return;
    try {
      const res = await fetch(`/api/v1/api-keys/${id}/revoke/`, { method: 'POST', headers });
      if (res.ok) {
        toast({ title: 'API key revoked', status: 'success', duration: 2000 });
        fetchKeys();
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    toast({ title: 'Copied to clipboard', status: 'success', duration: 1500 });
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.700">API Keys</Heading>
        <HStack>
          <IconButton icon={<FiRefreshCw />} onClick={fetchKeys} variant="ghost" aria-label="Refresh" />
          <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={onOpen}>New API Key</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={12}><Spinner size="lg" color="teal.500" /></Flex>
      ) : (
        <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Key</Th>
                <Th>Organization</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {keys.map((k) => (
                <Tr key={k.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="500">{k.name}</Td>
                  <Td>
                    <Flex align="center" gap={2}>
                      <Text fontFamily="mono" fontSize="xs" color="gray.500">
                        {k.key.substring(0, 8)}...
                      </Text>
                      <IconButton size="xs" icon={<FiCopy />} variant="ghost"
                        onClick={() => copyKey(k.key)} aria-label="Copy key" />
                    </Flex>
                  </Td>
                  <Td color="gray.500" fontSize="sm">{k.organization_name || '—'}</Td>
                  <Td><Badge colorScheme={k.is_active ? 'green' : 'red'}>{k.is_active ? 'Active' : 'Revoked'}</Badge></Td>
                  <Td fontSize="xs" color="gray.500">{new Date(k.created_at).toLocaleDateString()}</Td>
                  <Td>
                    {k.is_active && (
                      <IconButton size="xs" icon={<FiSlash />} variant="ghost" colorScheme="red"
                        onClick={() => revokeKey(k.id)} aria-label="Revoke" />
                    )}
                  </Td>
                </Tr>
              ))}
              {keys.length === 0 && (
                <Tr><Td colSpan={6} textAlign="center" py={8} color="gray.400">No API keys yet</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create API Key</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Production Key" focusBorderColor="teal.400" />
              </FormControl>
              <FormControl>
                <FormLabel>Organization (optional)</FormLabel>
                <Select value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })}
                  placeholder="Select organization">
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={createKey} isDisabled={!form.name}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
